// The theme control in the top bar.
//
// An icon button rather than a labelled switch or a menu: this sits in permanent
// chrome that agents look past a thousand times a day, and it is worth roughly
// one click a month. Two states only — see the comment on `toggle()` in
// ../theme/ThemeProvider.jsx for why there is no third "system" position.
//
// Accessibility, since an icon carries no text:
//   * aria-label names the ACTION ("Switch to dark theme"), not the state, so a
//     screen-reader user hears what pressing it will do.
//   * title repeats it for sighted mouse users, who get no other clue.
//   * aria-live="polite" on a visually hidden span announces the result, because
//     the only feedback otherwise is a colour change.
//   * It is a real <button>, so it is tabbable, and it keeps a visible
//     focus-visible ring — an icon button with the outline removed is unusable
//     from the keyboard.

import { useTheme } from './theme/ThemeProvider.jsx';

function SunIcon() {
  return (
    <svg viewBox="0 0 20 20" className="size-5" fill="currentColor" aria-hidden="true">
      <path d="M10 3a.75.75 0 0 1 .75.75v1a.75.75 0 0 1-1.5 0v-1A.75.75 0 0 1 10 3Zm0 11.5a.75.75 0 0 1 .75.75v1a.75.75 0 0 1-1.5 0v-1a.75.75 0 0 1 .75-.75ZM16.25 10a.75.75 0 0 1-.75.75h-1a.75.75 0 0 1 0-1.5h1a.75.75 0 0 1 .75.75Zm-11.5 0a.75.75 0 0 1-.75.75h-1a.75.75 0 0 1 0-1.5h1a.75.75 0 0 1 .75.75Zm9.31-4.56a.75.75 0 0 1 0 1.06l-.7.71a.75.75 0 1 1-1.07-1.06l.71-.71a.75.75 0 0 1 1.06 0ZM6.15 12.79a.75.75 0 0 1 0 1.06l-.71.71a.75.75 0 0 1-1.06-1.06l.71-.71a.75.75 0 0 1 1.06 0Zm7.7 1.06a.75.75 0 0 1-1.06 0l-.71-.71a.75.75 0 1 1 1.06-1.06l.71.71a.75.75 0 0 1 0 1.06ZM6.15 7.21a.75.75 0 0 1-1.06 0l-.71-.71a.75.75 0 0 1 1.06-1.06l.71.7a.75.75 0 0 1 0 1.07ZM10 6.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" className="size-5" fill="currentColor" aria-hidden="true">
      <path d="M8.28 2.28a.75.75 0 0 1 .18.78 6.5 6.5 0 0 0 8.48 8.48.75.75 0 0 1 .96.96A8 8 0 1 1 7.5 2.1a.75.75 0 0 1 .78.18Z" />
    </svg>
  );
}

export default function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  const action = isDark ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={action}
        title={action}
        className="grid size-9 place-items-center rounded-md text-ink-secondary
                   transition-colors hover:bg-sunken hover:text-ink
                   focus-visible:outline-2 focus-visible:outline-offset-2
                   focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
      >
        {/* The icon shows the theme you would move TO, matching the label. */}
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>
      <span aria-live="polite" className="sr-only">
        {isDark ? 'Dark theme' : 'Light theme'}
      </span>
    </>
  );
}
